import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Award, MapPin, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { founder, capabilities } from '../mock';
import SectionLabel from '../components/SectionLabel';
import Reveal from '../components/Reveal';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div>
      <section className="pt-20 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1320px] mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <SectionLabel icon={GraduationCap}>ABOUT THE PRACTICE</SectionLabel>
            <h1 className="mt-4 font-serif text-white text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Independent, senior-led <span className="text-orange-400 italic text-4xl md:text-5xl">infrastructure advisory.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
              A senior infrastructure practice with over 6 years of direct field and program-level experience across Australian arterial road networks. Chainage Advisory was established to deliver independent, senior-led governance advisory rooted in operational reality.
            </p>
            <p className="mt-4 max-w-2xl text-slate-400 leading-relaxed">{founder.bio}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-md text-xs font-mono text-slate-300"><MapPin className="w-3.5 h-3.5 text-orange-400" />{founder.location}</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-md text-xs font-mono text-slate-300"><Award className="w-3.5 h-3.5 text-orange-400" />{founder.yearsExperience} yrs experience</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 rounded-md text-xs font-mono text-slate-300"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />Victoria&rsquo;s Road Management &middot; MASH &middot; Austroads</span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <Reveal>
              <div className="rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6">
                <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300 mb-5">CORE PRACTICE PRINCIPLES</div>
                <ul className="space-y-4">
                  {[
                    'Senior-led, no junior handoffs',
                    'Independent, unconflicted advice',
                    'Evidence over abstraction',
                    'Audit-defensible documentation',
                  ].map((p, i) => (
                    <motion.li
                      key={p} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-3 text-sm text-slate-200"
                    >
                      <CheckCircle2 className="w-4 h-4 text-orange-400 mt-0.5" /> {p}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/5">
        <div className="max-w-[1320px] mx-auto px-6">
          <SectionLabel>OPERATIONAL DEPTH</SectionLabel>
          <h2 className="mt-4 font-serif text-white text-3xl md:text-4xl leading-tight">A practice grounded in the standards you’re accountable to.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {capabilities.map((c, i) => (
              <motion.div key={c.key}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
                className="rounded-lg border border-white/8 bg-white/[0.02] p-5 hover-ring transition-all"
              >
                <div className="text-white font-medium">{c.key}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors">
              Discuss an Engagement
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
